import { Dictionary, keyBy } from 'lodash';
import { useState, useCallback, useEffect, ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Configuration } from '../../clients/quipli-service/configuration/configuration';
import {
  AssignDriverRequest,
  Dispatch,
  Order,
  OrderProduct,
  OrderReturnStatus,
  OrderStatus,
  OrderType,
  SearchDispatchRequest,
} from '../../clients/quipli-service/order/order';
import { useAPI } from '../../clients/quipli-service/quipli-service-client';
import useRequestQueryParams from '../../hooks/use-request-query-params';
import { DateTime, Interval } from 'luxon';
import { toast } from '../../util/toasts';
import { useAdminApiV1 } from '../../clients/quipli-service/admin/admin-api-v1';
import { QuipliLink } from '../../components/link/quipli-link';
import { Typography } from '../../components/typography/typography';
import {
  AddressCell,
  AssignedUnitCell,
  CustomerNameCell,
  CustomerPhoneNumberCell,
  DateTimeCell,
  OrderNumberCell,
} from '../order/order-table-cells';
import { Location } from '../../clients/quipli-service/location/location';
import { SelectForm } from '../../components/select-form/select-form';
import { DataTable, DataTableColumn, DataTableRow } from '../../components/table/data-table';
import { Spinner } from '../../components/spinner/spinner';
import './dispatch.scss';
import Icon from '../../components/icons/icon';
import { Button } from '../../components/form/button';
import { Admin } from '../../clients/quipli-service/admin/admin';
import { SearchBar } from '../../components/search-bar/search-bar';
import { Dropdown } from '../../components/dropdown/dropdown';
import FileSaver from 'file-saver';
import { DatePicker } from '../../components/date-picker/date-picker';
import { Modal } from '../../components/modal/modal';
import { ScheduledTimeForm } from './components/scheduled-time-form';
import { SpinnerModal } from '../../components/modal/spinner-modal';
import { DispatchStatusTransitionButton } from '../dispatch-v2/dispatch-status-transition-button';

function extractDispatchSearchParams(params: URLSearchParams) {
  return {
    keyword: params.has('keyword') ? params.get('keyword')! : undefined,
    date: params.has('date') ? DateTime.fromISO(params.get('date')!) : DateTime.local(),
    driverIds: params.has('driverId') ? [parseInt(params.get('driverId')!)] : undefined,
    locationId: params.has('locationId') ? parseInt(params.get('locationId')!) : undefined,
    pageSize: params.has('pageSize') ? parseInt(params.get('pageSize')!) : 100,
    pageNumber: params.has('pageNumber') ? parseInt(params.get('pageNumber')!) : 1,
    type: params.has('type') ? (params.get('type')! as 'Delivery' | 'Pickup') : undefined,
  };
}

export const ScheduleTimeModal = ({
  setRefresh,
  isOpen,
  setIsOpen,
  dispatch,
  locations,
}: {
  setRefresh: (refresh: boolean) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  dispatch?: Dispatch;
  locations: Location[];
}) => {
  const onScheduledTimeUpdate = () => {
    toast('Successfully updated scheduled time for order');
    setRefresh(true);
    setIsOpen(false);
  };

  const onScheduledTimeError = () => {
    toast('Error updating the scheduled time', 'error');
    setIsOpen(false);
  };

  return (
    <>
      <Modal title={`Update scheduled time`} isOpen={isOpen} close={() => setIsOpen(false)} overflow="visible">
        {dispatch && (
          <ScheduledTimeForm
            orderId={dispatch.orderId}
            orderProductIds={{
              delivery: dispatch.products.delivery?.length ? dispatch.products.delivery.map((p) => p.id) : undefined,
              pickup: dispatch.products.pickup?.length ? dispatch.products.pickup.map((p) => p.id) : undefined,
            }}
            scheduledTime={dispatch.scheduledTime}
            timezone={locations.find((l) => l.id === dispatch.locationId)?.timezone}
            disabledIntervals={
              dispatch.products.delivery?.length
                ? [Interval.after(dispatch.startTime, { years: 10 })]
                : [Interval.before(dispatch.startTime, { years: 10 })]
            }
            update={() => onScheduledTimeUpdate()}
            error={() => onScheduledTimeError()}
          />
        )}
      </Modal>
    </>
  );
};

export const DispatchTable = ({
  request,
  drivers,
  locations,
  dispatchData,
  setRefresh,
}: {
  request: SearchDispatchRequest;
  drivers: Dictionary<Admin>;
  locations: Location[];
  dispatchData: Dispatch[];
  refresh: boolean;
  setRefresh: (refresh: boolean) => void;
}) => {
  const { getMultipleConfigurations, assignDriver } = useAPI();

  const [loading, setLoading] = useState<boolean>(false);
  const [unitTracking, setUnitTracking] = useState<string>();
  const [onlyOffrentPickup, setOnlyOffrentPickup] = useState<boolean>(false);
  const [tableData, setTableData] = useState<DataTableRow[]>([]);

  const [selectedDispatch, setSelectedDispatch] = useState<Dispatch>();

  const [isScheduledTimeModalOpen, setIsScheduledTimeModalOpen] = useState<boolean>(false);

  useEffect(() => {
    const init = async () => {
      const config = await getMultipleConfigurations(['CONF_UNIT_TRACKING_VIA', 'CONF_ONLY_OFFRENT_DISPATCH']);
      setUnitTracking(config.find((_) => _.variable == 'CONF_UNIT_TRACKING_VIA')?.value);
      setOnlyOffrentPickup(config.find((_) => _.variable == 'CONF_ONLY_OFFRENT_DISPATCH')?.value === '1');
    };
    init();
  }, []);

  const saveDriver = async (
    orderId: number,
    products: { delivery?: OrderProduct[]; pickup?: OrderProduct[] },
    driverId?: number,
  ) => {
    setLoading(true);
    try {
      const body: AssignDriverRequest = {
        orderId: orderId,
        orderProductIds: {
          delivery: products.delivery?.length ? products.delivery.map((p) => p.id) : undefined,
          pickup: products.pickup?.length ? products.pickup.map((p) => p.id) : undefined,
        },
        driverId,
      };

      await assignDriver(orderId, body);
      setRefresh(true);
      toast('Successfully updated driver for order');
    } catch (err) {
      console.error(err);
      toast('Error assigning the driver', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getClassForStatusLabel = (
    type: 'delivery' | 'pickup',
    onlyOffrentPickup: boolean,
    orderProductStatus?: string,
  ) => {
    switch (orderProductStatus) {
      case OrderReturnStatus.OnRent:
        return type === 'delivery'
          ? ['completed', 'Completed']
          : onlyOffrentPickup
          ? ['not-started', 'Not Started']
          : ['not-started', 'Ready for Pickup'];
      case OrderReturnStatus.OffRent:
        return type === 'delivery' ? ['completed', 'Completed'] : ['not-started', 'Ready for Pickup'];
      case OrderReturnStatus.Complete:
      case 'Overdue':
        return [orderProductStatus.toLowerCase(), orderProductStatus];
      default:
        return ['not-started', 'Not Started'];
    }
  };

  const buildDataRow = (
    row: Dispatch,
    trackingVia: 'serialNumber' | 'unitNumber',
    _locations: Location[],
    _drivers: Dictionary<Admin>,
    idx: number,
    _request: SearchDispatchRequest,
    onlyOffrentPickup: boolean,
  ) => {
    const returnStatus = getClassForStatusLabel(
      !row.products.delivery ? 'pickup' : 'delivery',
      onlyOffrentPickup,
      _request.date.startOf('day').toMillis() === DateTime.now().startOf('day').toMillis() &&
        row.startTime < _request.date.startOf('day')
        ? 'Overdue'
        : (row.products.delivery ?? row.products.pickup)![0].status,
    );
    return {
      mobileHeader: (
        <>
          <Typography type="text-md" weight="medium">
            {row.user.name}
          </Typography>
          <Typography type="text-sm" weight="medium">
            <Typography type="text-sm" weight="regular" className="inline text-[var(--grey-500)]">
              Order #{' '}
            </Typography>
            <QuipliLink
              to={`orders/view/${row.orderId}`}
              className="underline underline-offset-1 text-[var(--quipli-black-800)]"
            >
              {row.orderNumber || 'N/A'}
            </QuipliLink>
            <Typography type="text-sm" weight="medium">
              {row.products.delivery?.length && row.products.pickup?.length
                ? 'Delivery and Pickup'
                : row.products.delivery?.length && row.products.delivery.every((p) => p.type === OrderType.Sale)
                ? 'Sale Delivery'
                : row.products.delivery?.length
                ? 'Delivery'
                : 'Pickup'}
            </Typography>
            <Typography type="text-sm" weight="regular">
              Driver: {row.driver?.adminFullName ?? 'Unassigned'}
            </Typography>
          </Typography>
        </>
      ),
      fields: {
        orderId: row.orderId,
        orderNumber: <OrderNumberCell order={{ id: row.orderId, orderNumber: row.orderNumber } as Order} />,
        customer: (
          <>
            <CustomerNameCell order={{ user: row.user } as Order} />{' '}
            {row.representative && <Typography type="text-xs">Rep: {row.representative.name}</Typography>}
          </>
        ),
        phone: (
          <>
            <CustomerPhoneNumberCell order={{ user: row.user } as Order} />
            {row.representative && row.representative.phone && (
              <Typography type="text-xs">
                Rep. Phone:
                <CustomerPhoneNumberCell order={{ user: { ...row.user, phone: row.representative.phone } } as Order} />
              </Typography>
            )}
          </>
        ),
        type: (
          <Typography type="text-sm" weight="medium">
            {row.products.delivery?.length && row.products.pickup?.length
              ? 'Delivery and Pickup'
              : row.products.delivery?.length && row.products.delivery.every((p) => p.type === OrderType.Sale)
              ? 'Sale Delivery'
              : row.products.delivery?.length
              ? 'Delivery'
              : 'Pickup'}
          </Typography>
        ),
        time: <DateTimeCell date={row.startTime} />,
        products: (
          <div>
            {row.products.pickup &&
              row.products.pickup.length &&
              row.products.delivery &&
              row.products.delivery.length && <div className="text-sm font-semibold">Deliver:</div>}
            {row.products.delivery && row.products.delivery.length && (
              <AssignedUnitCell
                order={
                  { type: OrderType.Rental, status: OrderStatus.Approved, products: row.products.delivery } as Order
                }
                trackingVia={trackingVia}
                locations={_locations}
                isOrderList={false}
              />
            )}

            {row.products.pickup &&
              row.products.pickup.length &&
              row.products.delivery &&
              row.products.delivery.length && <div className="text-sm font-semibold mt-3">Pickup:</div>}

            {row.products.pickup && row.products.pickup.length && (
              <AssignedUnitCell
                order={{ type: OrderType.Rental, status: OrderStatus.Approved, products: row.products.pickup } as Order}
                trackingVia={unitTracking === '1' ? 'serialNumber' : 'unitNumber'}
                locations={_locations}
                isOrderList={true}
              />
            )}
          </div>
        ),

        address: <AddressCell address={row.address} />,
        driver: (
          <div id={`select-driver-${row.orderId}-${row.startTime}`}>
            <SelectForm
              maxMenuHeight={200}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              className="min-w-[250px] w-full"
              isClearable={true}
              isOptionSelected={(o) => row.driver?.adminId === o.adminId}
              options={Object.values(_drivers).map((d) => ({ ...d }))}
              value={row.driver && _drivers[row.driver?.adminId]}
              getOptionLabel={(o) => o.adminFullName}
              onChange={(driver) => saveDriver(row.orderId, row.products, driver?.adminId)}
              isDisabled={
                loading ||
                (row.products.delivery?.length
                  ? [OrderReturnStatus.Complete, OrderReturnStatus.OnRent, OrderReturnStatus.OffRent].includes(
                      row.products.delivery[0].status ?? OrderReturnStatus.NotStarted,
                    )
                  : [OrderReturnStatus.Complete].includes(row.products.pickup![0].status ?? OrderReturnStatus.OnRent))
              }
            />
          </div>
        ),
        scheduledTime: (
          <div className="min-w-full min-h-full flex">
            {row.scheduledTime && row.scheduledTime.isValid ? (
              <div className="flex">
                <div className="w-1/2 mr-6">
                  <DateTimeCell date={row.scheduledTime} />
                </div>
                <div className="w-1/2 ml-1">
                  <button
                    className="button button-yellow button-primary mb-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedDispatch(row);
                      setIsScheduledTimeModalOpen(true);
                      // displayScheduledTimeModalForRow(row, _locations);
                    }}
                  >
                    <Typography type="text-sm" weight="semi-bold" className="text-quipli-black-800">
                      Update
                    </Typography>
                  </button>
                </div>
              </div>
            ) : (
              <button
                className="button button-yellow button-primary mb-2"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedDispatch(row);
                  setIsScheduledTimeModalOpen(true);
                  // displayScheduledTimeModalForRow(row, _locations);
                }}
              >
                <Typography type="text-sm" weight="semi-bold" className="text-quipli-black-800">
                  Set Scheduled Time
                </Typography>
              </button>
            )}
          </div>
        ),
        status: (
          <div className={`w-fit px-[12px] py-[5px] rounded-2xl order-status-cell ${returnStatus[0]}`}>
            <Typography type="text-sm" weight="regular">
              {returnStatus[1]}
            </Typography>
          </div>
        ),
        actions: (
          <DispatchStatusTransitionButton
            orderDispatch={row}
            reload={() => {
              setRefresh(true);
            }}
          />
        ),
        rowNum: idx,
      },
    };
  };

  const colNames: DataTableColumn[] = [
    { label: 'Order Number', key: 'orderNumber' },
    { label: 'Customer', key: 'customer' },
    { label: 'Tel. No.', key: 'phone' },
    { label: 'Type', key: 'type' },
    { label: 'Time', key: 'time' },
    { label: 'Address', key: 'address' },
    { label: 'Driver', key: 'driver' },
    { label: 'Scheduled Time', key: 'scheduledTime' },
    { label: 'Products', key: 'products' },
    { label: 'Status', key: 'status' },
    { label: 'Actions', key: 'actions' },
  ];

  useEffect(() => {
    setTableData(
      dispatchData.map((item, idx) =>
        buildDataRow(
          item,
          unitTracking === '1' ? 'serialNumber' : 'unitNumber',
          locations,
          drivers,
          idx,
          request,
          onlyOffrentPickup,
        ),
      ),
    );
  }, [dispatchData, onlyOffrentPickup]);

  return (
    <>
      <DataTable columnNames={colNames} data={tableData} />
      <ScheduleTimeModal
        setRefresh={setRefresh}
        isOpen={isScheduledTimeModalOpen}
        setIsOpen={setIsScheduledTimeModalOpen}
        dispatch={selectedDispatch}
        locations={locations}
      />
    </>
  );
};

export const DispatchList = () => {
  const [loading, setLoading] = useState<boolean>(false),
    [locations, setLocations] = useState<Location[]>([]),
    [configs, setConfigs] = useState<Dictionary<Configuration>>({}),
    [tableData, setTableData] = useState<DataTableRow[]>([]),
    [drivers, setDrivers] = useState<Dictionary<Admin>>(),
    [refresh, setRefresh] = useState<boolean>(false),
    [isExporting, setIsExporting] = useState<boolean>(false),
    [updateScheduleTimeModal, setUpdateScheduleTimeModal] = useState<ReactNode | undefined>();

  const { searchDispatch, assignDriver, listLocations, getMultipleConfigurations, exportDispatchList } = useAPI();
  const { getAdminsList } = useAdminApiV1();

  useEffect(() => {
    const init = async () => {
      const [_locations, _configs, _drivers] = await Promise.all([
        listLocations(),
        getMultipleConfigurations(['CONF_UNIT_TRACKING_VIA', 'CONF_ONLY_OFFRENT_DISPATCH']),
        getAdminsList(),
      ]);
      setLocations(_locations.filter((loc) => loc.isActive));
      setConfigs(keyBy(_configs, 'variable'));
      setDrivers(keyBy(_drivers, 'adminId'));
    };
    init();
  }, []);

  const doSearchDispatch = useCallback(
    (_request: SearchDispatchRequest) => {
      if (configs && locations.length && drivers) {
        setLoading(true);

        searchDispatch({
          ..._request,
        })
          .then((items) => {
            setTableData(
              items.map((item, idx) =>
                buildDataRow(
                  item,
                  configs['CONF_UNIT_TRACKING_VIA']?.value === '1' ? 'serialNumber' : 'unitNumber',
                  locations,
                  drivers,
                  idx,
                  _request,
                  configs['CONF_ONLY_OFFRENT_DISPATCH']?.value === '1',
                ),
              ),
            );
            setLoading(false);
            setRefresh(false);
          })
          .catch((e) => {
            toast(e.message, 'error');
            setLoading(false);
            setTableData([]);
            setRefresh(false);
          });
      }
    },
    [configs, locations, drivers],
  );

  // List request hooks
  const [params] = useSearchParams();
  const { request, setRequest } = useRequestQueryParams<SearchDispatchRequest>(
    extractDispatchSearchParams(params),
    doSearchDispatch,
    [configs, locations, drivers],
  );

  const colNames: DataTableColumn[] = [
    { label: 'Order Number', key: 'orderNumber' },
    { label: 'Customer', key: 'customer' },
    { label: 'Tel. No.', key: 'phone' },
    { label: 'Type', key: 'type' },
    { label: 'Time', key: 'time' },
    { label: 'Address', key: 'address' },
    { label: 'Driver', key: 'driver' },
    { label: 'Scheduled Time', key: 'scheduledTime' },
    { label: 'Products', key: 'products' },
    { label: 'Status', key: 'status' },
  ];

  const getClassForStatusLabel = (
    type: 'delivery' | 'pickup',
    onlyOffrentPickup: boolean,
    orderProductStatus?: string,
  ) => {
    console.log(type, orderProductStatus);
    switch (orderProductStatus) {
      case OrderReturnStatus.OnRent:
        return type === 'delivery'
          ? ['completed', 'Completed']
          : onlyOffrentPickup
          ? ['not-started', 'Not Started']
          : ['not-started', 'Ready for Pickup'];
      case OrderReturnStatus.OffRent:
        return type === 'delivery' ? ['completed', 'Completed'] : ['not-started', 'Ready for Pickup'];
      case OrderReturnStatus.Complete:
      case 'Overdue':
        return [orderProductStatus.toLowerCase(), orderProductStatus];
      default:
        return ['not-started', 'Not Started'];
    }
  };

  useEffect(() => {
    if (refresh) doSearchDispatch(request);
  }, [refresh]);

  const saveDriver = async (
    orderId: number,
    products: { delivery?: OrderProduct[]; pickup?: OrderProduct[] },
    driverId?: number,
  ) => {
    setLoading(true);
    try {
      const body: AssignDriverRequest = {
        orderId: orderId,
        orderProductIds: {
          delivery: products.delivery?.length ? products.delivery.map((p) => p.id) : undefined,
          pickup: products.pickup?.length ? products.pickup.map((p) => p.id) : undefined,
        },
        driverId,
      };

      await assignDriver(orderId, body);
      setRefresh(true);
      toast('Successfully updated driver for order');
    } catch (err) {
      console.error(err);
      toast('Error assigning the driver', 'error');
    } finally {
      setLoading(false);
    }
  };

  const onScheduledTimeUpdate = () => {
    toast('Successfully updated scheduled time for order');
    setRefresh(true);
    setUpdateScheduleTimeModal(undefined);
  };

  const onScheduledTimeError = () => {
    toast('Error updating the scheduled time', 'error');
    setUpdateScheduleTimeModal(undefined);
  };

  const exportList = async () => {
    setIsExporting(true);
    try {
      const file = await exportDispatchList(request);
      const exportFileName = `Dispatch_${DateTime.local().toFormat('D').replaceAll('/', '-')}`;
      const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
      const fileExtension = '.xlsx';

      const data = new Blob([file], { type: fileType });
      FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);
    } catch (e) {
      console.error(e);
      toast('Error exporting the dispatch list', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const displayScheduledTimeModalForRow = (dispatch: Dispatch, locations: Location[]) => {
    setUpdateScheduleTimeModal(
      <Modal
        title={`Update scheduled time`}
        isOpen={true}
        close={() => setUpdateScheduleTimeModal(undefined)}
        overflow="visible"
      >
        <ScheduledTimeForm
          orderId={dispatch.orderId}
          orderProductIds={{
            delivery: dispatch.products.delivery?.length ? dispatch.products.delivery.map((p) => p.id) : undefined,
            pickup: dispatch.products.pickup?.length ? dispatch.products.pickup.map((p) => p.id) : undefined,
          }}
          scheduledTime={dispatch.scheduledTime}
          timezone={locations.find((l) => l.id === dispatch.locationId)?.timezone}
          disabledIntervals={
            dispatch.products.delivery?.length
              ? [Interval.after(dispatch.startTime, { years: 10 })]
              : [Interval.before(dispatch.startTime, { years: 10 })]
          }
          update={() => onScheduledTimeUpdate()}
          error={() => onScheduledTimeError()}
        />
      </Modal>,
    );
  };
  const buildDataRow = (
    row: Dispatch,
    trackingVia: 'serialNumber' | 'unitNumber',
    _locations: Location[],
    _drivers: Dictionary<Admin>,
    idx: number,
    _request: SearchDispatchRequest,
    onlyOffrentPickup: boolean,
  ) => {
    const returnStatus = getClassForStatusLabel(
      !row.products.delivery ? 'pickup' : 'delivery',
      onlyOffrentPickup,
      _request.date.startOf('day').toMillis() === DateTime.now().startOf('day').toMillis() &&
        row.startTime < _request.date.startOf('day')
        ? 'Overdue'
        : (row.products.delivery ?? row.products.pickup)![0].status,
    );
    return {
      mobileHeader: (
        <>
          <Typography type="text-md" weight="medium">
            {row.user.name}
          </Typography>
          <Typography type="text-sm" weight="medium">
            <Typography type="text-sm" weight="regular" className="inline text-[var(--grey-500)]">
              Order #{' '}
            </Typography>
            <QuipliLink
              to={`orders/view/${row.orderId}`}
              className="underline underline-offset-1 text-[var(--quipli-black-800)]"
            >
              {row.orderNumber || 'N/A'}
            </QuipliLink>
            <Typography type="text-sm" weight="medium">
              {row.products.delivery?.length && row.products.pickup?.length
                ? 'Delivery and Pickup'
                : row.products.delivery?.length && row.products.delivery.every((p) => p.type === OrderType.Sale)
                ? 'Sale Delivery'
                : row.products.delivery?.length
                ? 'Delivery'
                : 'Pickup'}
            </Typography>
            <Typography type="text-sm" weight="regular">
              Driver: {row.driver?.adminFullName ?? 'Unassigned'}
            </Typography>
          </Typography>
        </>
      ),
      fields: {
        orderId: row.orderId,
        orderNumber: <OrderNumberCell order={{ id: row.orderId, orderNumber: row.orderNumber } as Order} />,
        customer: (
          <>
            <CustomerNameCell order={{ user: row.user } as Order} />{' '}
            {row.representative && <Typography type="text-xs">Rep: {row.representative.name}</Typography>}
          </>
        ),
        phone: (
          <>
            <CustomerPhoneNumberCell order={{ user: row.user } as Order} />
            {row.representative && row.representative.phone && (
              <Typography type="text-xs">
                Rep. Phone:
                <CustomerPhoneNumberCell order={{ user: { ...row.user, phone: row.representative.phone } } as Order} />
              </Typography>
            )}
          </>
        ),
        type: (
          <Typography type="text-sm" weight="medium">
            {row.products.delivery?.length && row.products.pickup?.length
              ? 'Delivery and Pickup'
              : row.products.delivery?.length && row.products.delivery.every((p) => p.type === OrderType.Sale)
              ? 'Sale Delivery'
              : row.products.delivery?.length
              ? 'Delivery'
              : 'Pickup'}
          </Typography>
        ),
        time: <DateTimeCell date={row.startTime} />,
        products: (
          <div>
            {row.products.pickup &&
              row.products.pickup.length &&
              row.products.delivery &&
              row.products.delivery.length && <div className="text-sm font-semibold">Deliver:</div>}
            {row.products.delivery && row.products.delivery.length && (
              <AssignedUnitCell
                order={
                  { type: OrderType.Rental, status: OrderStatus.Approved, products: row.products.delivery } as Order
                }
                trackingVia={trackingVia}
                locations={_locations}
                isOrderList={false}
              />
            )}

            {row.products.pickup &&
              row.products.pickup.length &&
              row.products.delivery &&
              row.products.delivery.length && <div className="text-sm font-semibold mt-3">Pickup:</div>}

            {row.products.pickup && row.products.pickup.length && (
              <AssignedUnitCell
                order={{ type: OrderType.Rental, status: OrderStatus.Approved, products: row.products.pickup } as Order}
                trackingVia={configs['CONF_UNIT_TRACKING_VIA']?.value === '1' ? 'serialNumber' : 'unitNumber'}
                locations={_locations}
                isOrderList={true}
              />
            )}
          </div>
        ),

        address: <AddressCell address={row.address} />,
        driver: (
          <div id={`select-driver-${row.orderId}-${row.startTime}`}>
            <SelectForm
              maxMenuHeight={200}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              className="min-w-[250px] w-full"
              isClearable={true}
              isOptionSelected={(o) => row.driver?.adminId === o.adminId}
              options={Object.values(_drivers).map((d) => ({ ...d }))}
              value={row.driver && _drivers[row.driver?.adminId]}
              getOptionLabel={(o) => o.adminFullName}
              onChange={(driver) => saveDriver(row.orderId, row.products, driver?.adminId)}
              isDisabled={
                loading ||
                (row.products.delivery?.length
                  ? [OrderReturnStatus.Complete, OrderReturnStatus.OnRent, OrderReturnStatus.OffRent].includes(
                      row.products.delivery[0].status ?? OrderReturnStatus.NotStarted,
                    )
                  : [OrderReturnStatus.Complete].includes(row.products.pickup![0].status ?? OrderReturnStatus.OnRent))
              }
            />
          </div>
        ),
        scheduledTime: (
          <div className="min-w-full min-h-full flex">
            {row.scheduledTime && row.scheduledTime.isValid ? (
              <div className="flex">
                <div className="w-1/2 mr-6">
                  <DateTimeCell date={row.scheduledTime} />
                </div>
                <div className="w-1/2 ml-1">
                  <button
                    className="button button-yellow button-primary mb-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      displayScheduledTimeModalForRow(row, _locations);
                    }}
                  >
                    <Typography type="text-sm" weight="semi-bold" className="text-quipli-black-800">
                      Update
                    </Typography>
                  </button>
                </div>
              </div>
            ) : (
              <button
                className="button button-yellow button-primary mb-2"
                onClick={(e) => {
                  e.stopPropagation();
                  displayScheduledTimeModalForRow(row, _locations);
                }}
              >
                <Typography type="text-sm" weight="semi-bold" className="text-quipli-black-800">
                  Set Scheduled Time
                </Typography>
              </button>
            )}
          </div>
        ),
        status: (
          <div className={`w-fit px-[12px] py-[5px] rounded-2xl order-status-cell ${returnStatus[0]}`}>
            <Typography type="text-sm" weight="regular">
              {returnStatus[1]}
            </Typography>
          </div>
        ),
        rowNum: idx,
      },
    };
  };

  return (
    <div className="dispatch px-4">
      {loading || !locations || !drivers ? (
        <Spinner />
      ) : (
        <>
          <div className="mb-4">
            <h1>Order Dispatch</h1>
            <div className="flex justify-between w-full pb-1 md:pb-4">
              <SearchBar
                className="w-full"
                data-cy="search-customers"
                placeholder="Search by customer name or product name"
                defaultValue={request.keyword}
                setRequest={(keyword) => setRequest({ ...request, ...keyword })}
              />

              <div className="flex flex-end">
                <Dropdown
                  trigger={() => (
                    <Button className="button-tertiary">
                      <Icon icon="DotsVertical" />
                    </Button>
                  )}
                >
                  <div
                    className="items-center gap-2"
                    onClick={() => {
                      exportList();
                    }}
                  >
                    <Icon icon="Export" size="medium" />
                    <Typography type="text-sm" className="text-quipli-black-800">
                      Export
                    </Typography>
                  </div>
                </Dropdown>
              </div>
            </div>

            <div id="dispatch-date-and-filters" className="">
              <div className="flex gap-3">
                <div className="flex gap-5 items-center pb-1">
                  <div className="flex items-center gap-3">
                    <Icon
                      icon="ForwardArrow"
                      className="prev-day cursor-pointer"
                      onClick={() =>
                        setRequest({ ...request, date: (request.date ?? DateTime.now()).minus({ day: 1 }) })
                      }
                    />
                    <div>
                      <DatePicker
                        value={request.date}
                        clearable={false}
                        onChange={(date) => {
                          setRequest({ ...request, date: date! });
                        }}
                      />
                    </div>
                    <Icon
                      className="cursor-pointer"
                      icon="ForwardArrow"
                      onClick={() =>
                        setRequest({ ...request, date: (request.date ?? DateTime.now()).plus({ day: 1 }) })
                      }
                    />
                  </div>
                </div>
                <Button
                  className="button button-secondary pb-1"
                  onClick={() => setRequest({ ...request, date: DateTime.now() })}
                >
                  Today
                </Button>
              </div>

              <div id="dispatch-filters">
                <SelectForm
                  className="h-full pb-1 min-w-[150px] w-full"
                  options={locations}
                  onChange={(loc) => {
                    setRequest((prevState) => ({ ...prevState, locationIds: [loc?.id ?? 0] }));
                  }}
                  getOptionLabel={(loc) => loc.name}
                  getOptionValue={(loc) => `${loc.id}`}
                  isClearable={true}
                  placeholder="Location"
                  value={
                    request.locationIds !== undefined && request.locationIds.length > 0
                      ? locations.find((l) => l.id.toString() === (request.locationIds?.[0]?.toString() ?? ''))
                      : undefined
                  }
                />

                <SelectForm
                  className="h-full pb-1  min-w-[150px] w-full"
                  options={Object.values(drivers)}
                  onChange={(d) => {
                    setRequest((prevState) => ({ ...prevState, driverId: d?.adminId }));
                  }}
                  getOptionLabel={(o) => o.adminFullName}
                  getOptionValue={(o) => `${o.adminId}`}
                  isClearable={true}
                  placeholder="Driver"
                  value={drivers[request.driverIds ? request.driverIds[0] : 0]}
                />

                <SelectForm
                  className="h-full pb-1 w-full"
                  options={[
                    { label: 'Delivery', value: 'Delivery' },
                    { label: 'Pickup', value: 'Pickup' },
                  ]}
                  value={
                    request.type && request.type === 'Delivery'
                      ? { label: 'Delivery', value: 'Delivery' }
                      : request.type
                      ? { label: 'Pickup', value: 'pickup' }
                      : undefined
                  }
                  onChange={(e) => {
                    setRequest((prev) => {
                      return { ...prev, type: e ? (e.value as 'Delivery' | 'Pickup') : undefined };
                    });
                  }}
                  isClearable={true}
                  placeholder="Type"
                />
              </div>
            </div>
          </div>

          <DataTable columnNames={colNames} data={tableData} />
          {updateScheduleTimeModal}
          <SpinnerModal isOpen={isExporting} contentDescription={'Exporting Dispatches'} />
        </>
      )}
    </div>
  );
};
